import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  signOut,
  sendEmailVerification,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  linkWithCredential,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
} from "firebase/auth";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  getDocs,
  query,
  where,
  collection,
  limit,
  waitForPendingWrites,
} from "firebase/firestore";
import { getStorage, ref, deleteObject } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import {
  extractPreferredUsername,
  userDocIdentityFields,
  canQueryUsersByEmail,
} from "./utils/signInIdentity";

// YOUR EXISTING CONFIG — prod defaults; override via REACT_APP_FIREBASE_* for staging/dev builds.
const BASE_URL = process.env.REACT_APP_API_URL || "https://analytics-server-652526389082.europe-west2.run.app";
// authDomain is the host the Firebase Auth popup opens against
// (https://<authDomain>/__/auth/handler) and the string Google echoes back as
// "Continue to ..." in the OAuth consent screen. Pointing it at our custom
// Hosting domain (app.ecointel.io) instead of the default
// eddp-ecodynamic-diagnostics.firebaseapp.com keeps the visible URL on-brand
// during sign-in. Prerequisites — must stay in place or Google sign-in breaks
// with redirect_uri_mismatch:
//   1. The OAuth 2.0 Web Client used by Firebase Auth (Firebase Console →
//      Authentication → Google → Web SDK configuration → Web client ID) has
//      both `https://app.ecointel.io` (Authorised JavaScript origin) and
//      `https://app.ecointel.io/__/auth/handler` (Authorised redirect URI)
//      registered in Google Cloud Console → APIs & Services → Credentials.
//   2. app.ecointel.io is a connected Firebase Hosting custom domain on
//      project eddp-ecodynamic-diagnostics with SSL provisioned, so the
//      reserved /__/auth/* paths resolve.
//   3. app.ecointel.io is in Firebase Console → Authentication → Settings →
//      Authorized domains.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyDYPqNiD-pFbyeWwTpKR2NGkDIJdxsDfgo",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "app.ecointel.io",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "eddp-ecodynamic-diagnostics",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "eddp-ecodynamic-diagnostics.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "652526389082",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:652526389082:web:09c7b63f41ca43ba135563"
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);

// Use the modern persistence API (`initializeFirestore` + `persistentLocalCache`)
// instead of the legacy `getFirestore(app)` + `enableIndexedDbPersistence(db)`
// pair. The legacy pair is known to trigger
//   "FIRESTORE INTERNAL ASSERTION FAILED: Unexpected state (ID: ca9)"
// in firebase-js-sdk v12.x when listener targets churn rapidly (e.g. React 18
// StrictMode + multiple onSnapshot listeners), because the IndexedDB cache and
// the watch stream end up disagreeing on target versions.
//
// The new API also uses a different IndexedDB schema, so legacy stale state
// from before this migration is effectively discarded — fresh cache, no
// "ve":-1 ghosts. `persistentMultipleTabManager` keeps the offline-for-field
// behaviour we wanted from the legacy call (multi-tab is now supported, the
// old "multiple tabs open" warning no longer applies).
//
// IMPORTANT: `initializeFirestore` MUST be called before any `getFirestore(app)`
// call against the same app. Firebase doesn't expose a way to migrate an
// already-created Firestore instance to a different cache, hence why this
// happens at module top-level.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});

export const storage = getStorage(app);
// Callable Cloud Functions live in europe-west2 (see functions/index.js). The
// region must match or httpsCallable resolves the wrong endpoint.
export const functions = getFunctions(app, "europe-west2");
export const googleProvider = new GoogleAuthProvider();

// Microsoft (Azure AD / personal Microsoft accounts) via the generic OAuth
// provider with the well-known 'microsoft.com' providerId. Enabled in Firebase
// Console → Authentication → Sign-in method → Microsoft. `prompt:
// select_account` lets users pick which Microsoft identity to use instead of
// silently reusing whatever is already signed in to the browser — important for
// consultants/enterprise users who hold multiple work + personal accounts.
//
// Prerequisites (mirror the Google note above; must stay in place or sign-in
// breaks with redirect_uri_mismatch / AADSTS errors):
//   1. The Azure AD app registration backing the Firebase Microsoft provider
//      lists `https://app.ecointel.io/__/auth/handler` (and the default
//      `https://eddp-ecodynamic-diagnostics.firebaseapp.com/__/auth/handler`)
//      as Redirect URIs.
//   2. app.ecointel.io is in Firebase Console → Authentication → Settings →
//      Authorized domains (already required for Google + magic link).
export const microsoftProvider = new OAuthProvider('microsoft.com');
// email + User.Read (openid + profile are implied). Entra work accounts often
// still omit the email claim; user docs are keyed on uid (Handoff 33).
microsoftProvider.addScope('email');
microsoftProvider.addScope('User.Read');
microsoftProvider.setCustomParameters({ prompt: 'select_account' });

// --- Multi-provider auth (Magic Link + Google + Microsoft) ---
// See FRONTEND_MULTI_PROVIDER_AUTH_v1 for the full design.

const EMAIL_FOR_SIGN_IN_KEY = 'eddp:emailForSignIn';
// Pending OAuth credential staged for auto-linking after a magic-link
// round-trip (the auth/account-exists-with-different-credential flow). Stores
// the credential's toJSON() — which includes `providerId` — so we can rehydrate
// the right provider on the callback regardless of which OAuth button was used.
//
// SECURITY: staged in sessionStorage, NOT localStorage. The payload contains
// live OAuth idToken/accessToken values (~1h TTL); sessionStorage confines
// them to the current tab and clears them when it closes, shrinking the XSS
// blast radius. Trade-off: if the user opens the magic link in a DIFFERENT
// tab/browser, the credential is absent there — which the callback already
// treats as non-fatal (linking simply retries on the next OAuth sign-in).
const PENDING_OAUTH_CRED_KEY = 'eddp:pendingOAuthCred';
// Legacy key — older sessions (pre-Microsoft) staged the Google credential
// in localStorage under this key. Still read (and always purged) on the
// callback for backward compatibility with a link flow started before this
// deploy. New flows write PENDING_OAUTH_CRED_KEY to sessionStorage only.
const PENDING_GOOGLE_CRED_KEY = 'eddp:pendingGoogleCred';
// UPN / preferred_username from the OAuth result, stashed so OnboardingGate
// self-heal can still write it if the first users/{uid} write is deferred.
const OAUTH_HANDLE_KEY = 'eddp:oauthPreferredUsername';

function stashPreferredUsername(uid, handle) {
  if (!uid || !handle) return;
  try {
    window.sessionStorage.setItem(OAUTH_HANDLE_KEY, JSON.stringify({
      uid,
      preferredUsername: handle,
    }));
  } catch (_) {}
}

function takeStashedPreferredUsername(uid) {
  try {
    const raw = window.sessionStorage.getItem(OAUTH_HANDLE_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    if (parsed?.uid === uid && parsed.preferredUsername) {
      window.sessionStorage.removeItem(OAUTH_HANDLE_KEY);
      return String(parsed.preferredUsername).trim();
    }
  } catch (_) {}
  return '';
}

/**
 * Builds the ActionCodeSettings passed to sendSignInLinkToEmail. The callback
 * URL must be in the Firebase Console authorised-domains list.
 *
 * Returned as a function (not a module-level const) so we compute it at call
 * time — eliminates any temporal-dead-zone risk from cross-module reference
 * order, and means the URL reflects the live window.origin (helpful if dev
 * tunnels / preview deploys ever rewrite it).
 */
function buildEmailLinkActionCodeSettings() {
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  return {
    url: `${origin}/auth/email-link-callback`,
    handleCodeInApp: true,
  };
}

/**
 * Retry an async Firestore op with exponential backoff. Targets the
 * timing-dependent failures seen on first sign-in:
 *   - `permission-denied` while the Firestore SDK's auth token is still
 *     propagating immediately after signInWithEmailLink/signInWithPopup
 *     (the rule `request.auth.uid == userId` evaluates against a not-yet-set
 *     token and rejects). This clears within a few hundred ms.
 *   - `unavailable` / transient network blips between the client and Firestore.
 *
 * Non-retryable errors (e.g. malformed data) are re-thrown immediately so we
 * don't mask real bugs behind a retry loop.
 */
const RETRYABLE_FIRESTORE_CODES = new Set([
  'permission-denied',
  'unavailable',
  'deadline-exceeded',
  'aborted',
  'internal',
]);

async function withRetry(fn, { attempts = 4, baseDelayMs = 250 } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const code = err?.code || '';
      const isRetryable = RETRYABLE_FIRESTORE_CODES.has(code);
      if (!isRetryable || i === attempts - 1) throw err;
      const delay = baseDelayMs * 2 ** i;
      console.warn(`Firestore op failed (${code}); retrying in ${delay}ms (attempt ${i + 1}/${attempts})`);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

/**
 * Block until queued Firestore writes have reached the server, but cap the
 * wait so an offline/poor connection can't hang the sign-in flow forever.
 *
 * With `persistentLocalCache`, `setDoc` resolves on the LOCAL IndexedDB commit
 * — the server sync happens asynchronously afterwards. If the user closes the
 * tab in that window the write is lost server-side (the symptom: Auth user
 * exists, `users/{uid}` doc never appears). Awaiting this after the user-doc
 * write makes the common (online) case durable before we navigate away.
 *
 * On timeout we resolve anyway (non-fatal): the write stays queued in the
 * local cache and will sync on reconnect; we just couldn't confirm it landed.
 */
async function flushPendingWrites(timeoutMs = 8000) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs);
  });
  try {
    const outcome = await Promise.race([waitForPendingWrites(db).then(() => 'flushed'), timeout]);
    if (outcome === 'timeout') {
      console.warn('flushPendingWrites: timed out waiting for server sync; write remains queued locally.');
    }
  } catch (err) {
    console.warn('flushPendingWrites:', err?.message || err);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Idempotent first-sign-in setup shared by Google and magic-link flows.
 *
 * On first sign-in this:
 *   1. Looks up an admin-pre-provisioned `pending_<email-hash>` placeholder
 *      by EMAIL (not uid). If found, copies its data into pendingData and
 *      deletes the placeholder.
 *   2. Auto-creates the personal org at `orgs/{uid}` (orgId === uid, type
 *      'personal'). Non-fatal if it fails — admin can repair org membership.
 *   3. Creates the `users/{uid}` doc with the canonical 25-field shape.
 *      Every field has a fallback to either `pendingData.<field>` or a
 *      sensible default. Add fields ONLY here — Dashboard, AdminSettings,
 *      and team-management UI all expect every field to be present.
 *
 * No-ops if `users/{uid}` already exists. This guard is what makes
 * second-provider sign-in (Google after magic-link, or vice versa, both
 * keep the same uid via linkWithCredential) safe.
 *
 * Note: `sendEmailVerification` is NOT called here — it's the caller's
 * responsibility. Magic-link sign-in skips it (Firebase already marks the
 * email verified); Google sign-in still sends one for unverified accounts.
 */
export async function ensureUserDocAndPersonalOrg(user, identityHints = {}) {
  if (!user) return;

  // Force-refresh the ID token so the Firestore SDK is writing with a fresh,
  // fully-propagated token. Without this, the very first getDoc/setDoc after
  // sign-in can race the token handshake and hit `permission-denied`. Best
  // effort — withRetry below also covers the residual race.
  try {
    await user.getIdToken(true);
  } catch (e) {
    console.warn('getIdToken(true) before user-doc setup failed (continuing):', e?.message || e);
  }

  const userRef = doc(db, 'users', user.uid);
  const userSnap = await withRetry(() => getDoc(userRef));
  if (userSnap.exists()) return;

  // Check for a pending placeholder created by admin (matched by email).
  // See FRONTEND_MULTI_PROVIDER_AUTH_v1 §C "Placeholder mechanic".
  // Only claim when email is verified and present — never query on empty
  // (Handoff 33). Do not match placeholders on UPN; no-email Entra users
  // are a fresh free-tier account.
  let pendingData = {};
  try {
    if (canQueryUsersByEmail(user)) {
      const q = query(collection(db, 'users'), where('email', '==', user.email));
      const pendingSnap = await getDocs(q);
      for (const d of pendingSnap.docs) {
        if (d.id !== user.uid) {
          pendingData = d.data();
          await deleteDoc(doc(db, 'users', d.id));
          break;
        }
      }
    }
  } catch (_) {}

  const identity = userDocIdentityFields(user, {
    preferredUsername: identityHints.preferredUsername || takeStashedPreferredUsername(user.uid),
    profile: identityHints.profile,
  });

  // Phase 0: personal org auto-created on first sign-in. orgId === uid.
  // If the user has been pre-provisioned by an admin into other orgs, those
  // memberships are preserved alongside the personal-org 'owner' role.
  const personalOrgId = user.uid;
  const personalOrgName =
    identity.displayName ||
    (identity.email && identity.email.split('@')[0]) ||
    'My account';
  const inheritedOrgMemberships = (pendingData.orgMemberships && typeof pendingData.orgMemberships === 'object')
    ? pendingData.orgMemberships
    : {};
  try {
    await setDoc(
      doc(db, 'orgs', personalOrgId),
      {
        ownerUid: user.uid,
        name: `${personalOrgName}'s account`,
        type: 'personal',
        createdAt: new Date().toISOString(),
      },
      { merge: true }
    );
  } catch (err) {
    // Non-fatal: org rules may reject if a doc with that id already exists in a
    // shape we can't merge into. The user doc creation still proceeds; admin
    // can repair org membership if needed.
    console.warn('Personal org creation skipped:', err?.message || err);
  }

  await withRetry(() => setDoc(userRef, {
    ...identity,
    role: pendingData.role || 'user',
    farmIds: pendingData.farmIds || [],
    businessName: pendingData.businessName || '',
    phoneNumber: pendingData.phoneNumber || '',
    subscriptionStatus: pendingData.subscriptionStatus || 'none',
    subscriptionOverride: pendingData.subscriptionOverride || false,
    overrideReason: pendingData.overrideReason || '',
    createdBy: pendingData.createdBy || '',
    createdAt: new Date().toISOString(),
    profileComplete: false,
    address: pendingData.address || '',
    jobTitle: pendingData.jobTitle || '',
    addressLine1: pendingData.addressLine1 || '',
    addressLine2: pendingData.addressLine2 || '',
    addressCity: pendingData.addressCity || '',
    addressRegion: pendingData.addressRegion || '',
    addressPostcode: pendingData.addressPostcode || '',
    addressCountry: pendingData.addressCountry || '',
    // Phase 0 fields:
    planTier: pendingData.planTier || 'free',
    personalOrgId,
    orgMemberships: { ...inheritedOrgMemberships, [personalOrgId]: { role: 'owner' } },
    reportGrants: pendingData.reportGrants || {},
    reportPackageOverrides: pendingData.reportPackageOverrides || [],
  }));

  // Block until the user-doc write has actually reached the Firestore servers
  // (bounded — see flushPendingWrites). Prevents the "Auth user exists but no
  // users/{uid} doc" state when the tab closes before the local-cache write
  // has synced. Idempotent: a second-provider sign-in returns early above
  // (doc already exists) and never reaches this point.
  await flushPendingWrites();
}

/**
 * Shared OAuth-popup sign-in for any federated provider (Google, Microsoft, …).
 * Returns either:
 *   { user, pendingEmailLink: false }                 — normal sign-in
 *   { user: null, pendingEmailLink: true, email }     — account-exists-with-
 *                                                       different-credential
 *                                                       was triggered; a magic
 *                                                       link has been sent and
 *                                                       the new provider's
 *                                                       credential is staged in
 *                                                       sessionStorage for
 *                                                       rehydration after the
 *                                                       email-link callback
 *                                                       completes. Caller should
 *                                                       surface a "check your
 *                                                       email to link <provider>"
 *                                                       UI.
 *
 * Provider-agnostic: the account-linking branch uses OAuthProvider's static
 * credentialFromError() (works for google.com, microsoft.com, etc.) and stores
 * the credential's toJSON() — which carries `providerId` — so the callback can
 * rehydrate the correct provider in completeEmailLinkSignIn().
 */
async function signInWithOAuthProvider(provider) {
  try {
    let result;
    try {
      result = await signInWithPopup(auth, provider);
    } catch (err) {
      if (err?.code === 'auth/account-exists-with-different-credential') {
        // FRONTEND_MULTI_PROVIDER_AUTH_v1 Decision #4: auto-link rather than
        // reject the second provider. Send a magic link to the existing email,
        // stash the new credential, and finish linking in
        // completeEmailLinkSignIn() after the email-link round-trip.
        const pendingCred = OAuthProvider.credentialFromError(err);
        const email = err.customData?.email;
        if (email && pendingCred) {
          await sendSignInLinkToEmail(auth, email, buildEmailLinkActionCodeSettings());
          try {
            // toJSON() on an OAuth credential returns:
            //   { providerId, signInMethod, idToken, accessToken, ... }
            // Tokens are ~1h-lived — rehydration may fail if the user takes too
            // long to click the magic link. Documented edge case.
            window.sessionStorage.setItem(PENDING_OAUTH_CRED_KEY, JSON.stringify(pendingCred.toJSON()));
            window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
          } catch (_) {}
          return { user: null, pendingEmailLink: true, email };
        }
      }
      throw err;
    }
    const user = result.user;
    const oauthProfile = getAdditionalUserInfo(result)?.profile || {};
    const preferredUsername = extractPreferredUsername(oauthProfile);
    stashPreferredUsername(user.uid, preferredUsername);

    await ensureUserDocAndPersonalOrg(user, { preferredUsername, profile: oauthProfile });

    if (user.email && !user.emailVerified) {
      try {
        await sendEmailVerification(user);
      } catch (e) {
        console.warn('sendEmailVerification:', e?.message || e);
      }
    }

    return { user, pendingEmailLink: false };
  } catch (error) {
    console.error("Login failed", error);
    throw error;
  }
}

/**
 * Google sign-in. Thin wrapper over signInWithOAuthProvider — see that helper
 * for the return shape and account-linking behaviour.
 */
export const loginWithGoogle = async () => signInWithOAuthProvider(googleProvider);

/**
 * Microsoft sign-in (Azure AD + personal Microsoft accounts). Same return shape
 * and auto-linking behaviour as loginWithGoogle.
 */
export const loginWithMicrosoft = async () => signInWithOAuthProvider(microsoftProvider);

/**
 * Email/password sign-in. Creates the user doc on first login if missing.
 */
export async function loginWithPassword(email, password) {
  const trimmed = String(email || '').trim();
  if (!trimmed) throw new Error('email_required');
  if (!password) throw new Error('password_required');
  const result = await signInWithEmailAndPassword(auth, trimmed, password);
  await ensureUserDocAndPersonalOrg(result.user);
  try {
    await updateDoc(doc(db, 'users', result.user.uid), {
      passwordMigrationComplete: true,
      passwordSetAt: new Date().toISOString(),
      passwordMigrationDeferredUntil: deleteField(),
    });
  } catch (e) {
    console.warn('passwordMigrationComplete on login:', e?.message || e);
  }
  return result.user;
}

/**
 * Email/password registration. Sends a verification email (non-blocking) so
 * the user can access the demo tier immediately while verification is pending.
 */
export async function registerWithPassword(email, password) {
  const trimmed = String(email || '').trim();
  if (!trimmed) throw new Error('email_required');
  if (!password) throw new Error('password_required');
  const result = await createUserWithEmailAndPassword(auth, trimmed, password);
  await ensureUserDocAndPersonalOrg(result.user);
  try {
    await updateDoc(doc(db, 'users', result.user.uid), {
      passwordMigrationComplete: true,
      passwordSetAt: new Date().toISOString(),
    });
  } catch (e) {
    console.warn('passwordMigrationComplete on register:', e?.message || e);
  }
  try {
    await sendEmailVerification(result.user);
  } catch (e) {
    console.warn('sendEmailVerification:', e?.message || e);
  }
  return result.user;
}

/**
 * Password-reset email for users who forgot their password.
 */
export async function resetPassword(email) {
  const trimmed = String(email || '').trim();
  if (!trimmed) throw new Error('email_required');
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  await sendPasswordResetEmail(auth, trimmed, {
    url: `${origin}/login`,
    handleCodeInApp: false,
  });
}

/**
 * Set a new password for the currently signed-in user (magic-link migration path).
 */
export async function updateUserPassword(newPassword) {
  const user = auth.currentUser;
  if (!user) throw new Error('not_signed_in');
  if (!newPassword) throw new Error('password_required');
  await updatePassword(user, newPassword);
}

/**
 * Magic-link migration: set a password on the current session and mark complete.
 * Uses updatePassword (not a reset email) per FRONTEND_MULTI_PROVIDER_AUTH migration plan.
 */
export async function completePasswordMigration(newPassword) {
  const user = auth.currentUser;
  if (!user) throw new Error('not_signed_in');
  await updateUserPassword(newPassword);
  await updateDoc(doc(db, 'users', user.uid), {
    passwordMigrationComplete: true,
    passwordSetAt: new Date().toISOString(),
    passwordMigrationDeferredUntil: deleteField(),
  });
}

/**
 * Snooze the migration prompt for signed-in magic-link users.
 */
export async function deferPasswordMigration(days = 7) {
  const user = auth.currentUser;
  if (!user) throw new Error('not_signed_in');
  const until = new Date();
  until.setDate(until.getDate() + days);
  await updateDoc(doc(db, 'users', user.uid), {
    passwordMigrationDeferredUntil: until.toISOString(),
  });
}

/**
 * Re-authenticate the current user with email/password before a sensitive change.
 */
export async function reauthenticateWithEmailPassword(email, password) {
  const user = auth.currentUser;
  if (!user) throw new Error('not_signed_in');
  const cred = EmailAuthProvider.credential(String(email || '').trim(), password);
  await reauthenticateWithCredential(user, cred);
}

/**
 * Step 1 of magic-link sign-in: send the user an email containing a sign-in link.
 * Caches the email locally so completeEmailLinkSignIn() can finish the round-trip
 * without re-prompting (unless the user opens the link in a different browser).
 */
export async function startEmailLinkSignIn(email) {
  const trimmed = String(email || '').trim();
  if (!trimmed) throw new Error('email_required');
  await sendSignInLinkToEmail(auth, trimmed, buildEmailLinkActionCodeSettings());
  try {
    window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, trimmed);
  } catch (_) {}
}

/**
 * Step 2 of magic-link sign-in. Call this on the /auth/email-link-callback route
 * after the user clicks the link in their inbox.
 *
 * Also rehydrates a pending Google credential (stashed by the account-linking
 * dispatch inside loginWithGoogle) and links it to the now-signed-in user.
 * Linking failure is non-fatal — the user is still signed in via the email
 * link, and Google linking will retry on the user's next Google sign-in attempt.
 *
 * @param {() => Promise<string|null>} [promptForEmail] Optional async fn used
 *   when the user opened the link in a different browser than they requested
 *   it from (so localStorage has no cached email). Defaults to window.prompt.
 */
export async function completeEmailLinkSignIn(promptForEmail) {
  if (!isSignInWithEmailLink(auth, window.location.href)) {
    throw new Error('not_an_email_link');
  }
  let email = null;
  try {
    email = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
  } catch (_) {}
  if (!email) {
    // User opened the link in a different browser than they requested it from.
    email = typeof promptForEmail === 'function'
      ? await promptForEmail()
      : (typeof window !== 'undefined' ? window.prompt('Please confirm the email you used to sign in') : null);
    if (!email) throw new Error('email_confirmation_required');
  }

  const result = await signInWithEmailLink(auth, email, window.location.href);
  try {
    window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
  } catch (_) {}

  // First-sign-in setup (placeholder lookup, personal org, user doc).
  // No-op if users/{uid} already exists.
  //
  // BEST-EFFORT — must NOT throw out of the callback. Immediately after
  // signInWithEmailLink resolves, the Firestore SDK has not always picked up
  // the freshly-minted auth credential yet, so this first write can fail with
  // `permission-denied` for a window longer than the in-helper retry budget
  // (observed: persists past ~1.75s of retries on a cold magic-link callback).
  // The user IS authenticated at this point, so sign-in must succeed
  // regardless: we swallow the error here and let OnboardingGate's self-heal
  // (which runs inside onAuthStateChanged, by which time auth has fully
  // propagated to Firestore) create the doc on the redirect to /dashboard.
  // The auth onCreate backstop (functions/index.js) is the final safety net.
  try {
    await ensureUserDocAndPersonalOrg(result.user);
  } catch (setupErr) {
    console.warn(
      'completeEmailLinkSignIn: deferred users/{uid} creation to OnboardingGate (non-fatal):',
      setupErr?.code || setupErr?.message || setupErr
    );
  }

  // Magic-link arrivals are already emailVerified=true — skip
  // sendEmailVerification to avoid a redundant email.

  let pending = null;
  try {
    // Current flows stage the credential in sessionStorage (same-tab only);
    // the two localStorage reads are legacy fallbacks for flows started
    // before the sessionStorage change / the multi-provider deploy.
    pending = window.sessionStorage.getItem(PENDING_OAUTH_CRED_KEY)
      || window.localStorage.getItem(PENDING_OAUTH_CRED_KEY)
      || window.localStorage.getItem(PENDING_GOOGLE_CRED_KEY);
  } catch (_) {}
  if (pending) {
    try {
      const cred = rehydrateOAuthCredential(JSON.parse(pending));
      if (cred) await linkWithCredential(result.user, cred);
    } catch (linkErr) {
      // Non-fatal: OAuth ID + access tokens are both ~1h-lived. If they've
      // expired by the time the user clicked the magic link, linkWithCredential
      // throws auth/invalid-credential. The user is still signed in via the
      // email link; the next sign-in attempt with that provider re-triggers
      // auth/account-exists-with-different-credential with fresh tokens that
      // will work. See FRONTEND_MULTI_PROVIDER_AUTH_v1 §B "Edge case to test".
      console.warn('OAuth credential link skipped:', linkErr?.message || linkErr);
    } finally {
      // Immediate cleanup in BOTH storages (legacy localStorage entries too)
      // — the staged tokens must never outlive the linking attempt.
      try {
        window.sessionStorage.removeItem(PENDING_OAUTH_CRED_KEY);
        window.localStorage.removeItem(PENDING_OAUTH_CRED_KEY);
        window.localStorage.removeItem(PENDING_GOOGLE_CRED_KEY);
      } catch (_) {}
    }
  }

  return result.user;
}

/**
 * Rebuild an AuthCredential from the toJSON() blob staged before a magic-link
 * round-trip (account-linking flow). Switches on `providerId` so the right
 * provider's credential is minted. Both token args are nullable/optional —
 * either an idToken or accessToken is sufficient to rehydrate.
 */
function rehydrateOAuthCredential(json) {
  if (!json) return null;
  const providerId = json.providerId || '';
  if (providerId === 'google.com') {
    // Firebase v9 modular SDK: GoogleAuthProvider.credential(idToken, accessToken).
    return GoogleAuthProvider.credential(json.idToken || null, json.accessToken || null);
  }
  if (providerId) {
    // Generic OAuth providers (microsoft.com, etc.).
    return new OAuthProvider(providerId).credential({
      idToken: json.idToken || undefined,
      accessToken: json.accessToken || undefined,
    });
  }
  return null;
}

export const logout = () => signOut(auth);

const KNOWN_FARM_SUBCOLLECTIONS = [
  'cache',
  'config',
  'connectedServiceEvents',
  'connectedServices',
  'farmCurves',
  'managementEvents',
  'meta',
  'parcelCurves',
  'parcels',
  'uploadedReports',
];

const deleteCollectionDocs = async (collectionRef, batchSize = 200) => {
  while (true) {
    const snap = await getDocs(query(collectionRef, limit(batchSize)));
    if (snap.empty) break;
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    if (snap.size < batchSize) break;
  }
};

// --- DELETE FUNCTION (PRESERVED) ---
export const deleteFarm = async (docId, fileId) => {
  try {
    // 1. Delete known nested farm subcollections first
    for (const subcollectionName of KNOWN_FARM_SUBCOLLECTIONS) {
      const subRef = collection(db, "farms", docId, subcollectionName);
      await deleteCollectionDocs(subRef);
    }

    // 2. Delete Database Record
    await deleteDoc(doc(db, "farms", docId));

    // 3. Delete Uploaded File (if it exists)
    if (fileId) {
       const fileRef = ref(storage, `${fileId}.kmz`);
       await deleteObject(fileRef).catch(err => {
         console.warn("File already gone or not found:", err.message);
       });
    }
  } catch (error) {
    console.error("Error deleting farm:", error);
    throw error;
  }
};

// --- MAP FETCHER (UPDATED URL) ---
export const getFarmMap = async (farmId) => {
  const url = `${BASE_URL}/get-farm-map?farmId=${farmId}`;
  
  try {
    // Get token for auth
    const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    
    const response = await fetch(url, { 
       method: "GET",
       headers: token ? { 'Authorization': `Bearer ${token}` } : {} 
    });

    if (response.status === 404 || response.status === 503) {
      throw new Error("Map Processing");
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || "Failed to fetch map");
    }
    return await response.json();
  } catch (error) {
    console.error("getFarmMap Error:", error);
    throw error;
  }
};

// Checkout has moved to api.js — use createCheckoutSession from there.
